<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import AdminLayout from '@/layouts/AdminLayout.vue'
import SessaoReplayer from '@/components/SessaoReplayer.vue'
import { getSessao, loadSessaoEvents } from '@/api/sessoes'
import { getApiErrorMessage } from '@/api/client'
import type { SessaoGravacao } from '@/types/sessoes'
import { formatDateTime } from '@/utils/datetime'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const route = useRoute()
const router = useRouter()

const leftId = computed(() => String(route.query.left ?? ''))
const rightId = computed(() => String(route.query.right ?? ''))

const leftSessao = ref<SessaoGravacao | null>(null)
const rightSessao = ref<SessaoGravacao | null>(null)
const leftEvents = ref<unknown[]>([])
const rightEvents = ref<unknown[]>([])
const loading = ref(true)

function label(sessao: SessaoGravacao | null): string {
  if (!sessao) return '—'
  return sessao.usuario_externo_nome || sessao.usuario_externo_id || sessao.id
}

async function loadSide(id: string): Promise<{ sessao: SessaoGravacao; events: unknown[] }> {
  const sessao = await getSessao(id)
  const events = await loadSessaoEvents(sessao.id, sessao.chunks_count)
  return { sessao, events }
}

async function load(): Promise<void> {
  if (!leftId.value || !rightId.value) {
    await Swal.fire({
      icon: 'warning',
      title: 'Seleção inválida',
      text: 'Selecione duas sessões na listagem para comparar.',
    })
    await router.replace('/admin/sessoes')
    return
  }

  loading.value = true
  try {
    const [left, right] = await Promise.all([
      loadSide(leftId.value),
      loadSide(rightId.value),
    ])
    leftSessao.value = left.sessao
    rightSessao.value = right.sessao
    leftEvents.value = left.events
    rightEvents.value = right.events
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Erro ao carregar comparação',
      text: getApiErrorMessage(error, 'Não foi possível abrir as sessões'),
    })
    await router.replace('/admin/sessoes')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <AdminLayout>
    <template #title>
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <RouterLink to="/admin/sessoes" class="small text-decoration-none">
            <i class="bi bi-arrow-left me-1" aria-hidden="true" />
            Sessões
          </RouterLink>
          <h1 class="h5 mb-1 mt-1">Comparar sessões</h1>
          <p class="text-muted small mb-0">Replay lado a lado</p>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-muted">Carregando replays…</div>

    <div v-else class="row g-3">
      <div class="col-12 col-xl-6">
        <div class="card shadow-sm border-0 mb-2">
          <div class="card-body py-2 small">
            <strong>{{ label(leftSessao) }}</strong>
            <span v-if="leftSessao" class="text-muted">
              · {{ formatDateTime(leftSessao.started_at) ?? '—' }}
            </span>
          </div>
        </div>
        <SessaoReplayer :events="leftEvents" compact :show-timeline="false" />
      </div>
      <div class="col-12 col-xl-6">
        <div class="card shadow-sm border-0 mb-2">
          <div class="card-body py-2 small">
            <strong>{{ label(rightSessao) }}</strong>
            <span v-if="rightSessao" class="text-muted">
              · {{ formatDateTime(rightSessao.started_at) ?? '—' }}
            </span>
          </div>
        </div>
        <SessaoReplayer :events="rightEvents" compact :show-timeline="false" />
      </div>
    </div>
  </AdminLayout>
</template>
